<template>
  <div class="feature-types-panel">
    <div class="feature-types-panel__head">
      <h3>Types de signalements</h3>
      <router-link
        v-if="permissions && permissions.can_update_project && isOnline"
        :to="{
          name: 'ajouter-type-signalement',
          params: { slug },
        }"
        class="feature-types-panel__add"
        data-tooltip="Créer un nouveau type de signalement"
        data-position="top center"
        data-test="add-feature-type"
        aria-label="Créer un nouveau type de signalement"
      >
        <img src="@/assets/img/project-detail/add.png" alt="" />
      </router-link>
    </div>
    <div id="feature_type-list" class="ui middle aligned divided list">
      <div :class="{ active: loading }" class="ui inverted dimmer">
        <div class="ui text loader">
          Récupération des types de signalements en cours...
        </div>
      </div>
      <div
        v-for="(type, index) in feature_types"
        :id="type.title"
        :key="type.title + '-' + index"
        class="item"
      >
        <div class="feature-type-container">
          <FeatureTypeLink :feature-type="type" />
          <div class="middle aligned content">
            <router-link
              v-if="
                project &&
                permissions &&
                permissions.can_create_feature &&
                !type.geom_type.includes('multi')
              "
              :to="{
                name: 'ajouter-signalement',
                params: { slug_type_signal: type.slug },
              }"
              class="ft-icon-btn"
              data-tooltip="Ajouter un signalement"
              data-position="top right"
              data-variation="mini"
              :data-test="`add-feature-for-${type.title}`"
            >
              <img src="@/assets/img/project-detail/add-feature.png" alt="" />
            </router-link>

            <router-link
              v-if="
                project &&
                permissions &&
                permissions.can_create_feature_type &&
                isOnline
              "
              :to="{
                name: 'dupliquer-type-signalement',
                params: { slug_type_signal: type.slug },
              }"
              class="ft-icon-btn"
              data-tooltip="Dupliquer un type de signalement"
              data-position="top right"
              data-variation="mini"
              :data-test="`duplicate-feature-type-for-${type.title}`"
            >
              <img src="@/assets/img/project-detail/duplicate.png" alt="" />
            </router-link>

            <div v-if="isImporting(type)" class="import-message">
              <i class="info circle icon" aria-hidden="true" />
              Import en cours
            </div>
            <template v-else>
              <router-link
                v-if="
                  project &&
                  permissions &&
                  permissions.can_create_feature_type &&
                  isOnline
                "
                :to="{
                  name: 'editer-affichage-signalement',
                  params: { slug_type_signal: type.slug },
                }"
                class="ft-icon-btn"
                data-tooltip="Éditer l'affichage du type de signalement"
                data-position="top center"
                data-variation="mini"
                :data-test="`edit-feature-type-display-for-${type.title}`"
              >
                <img src="@/assets/img/project-detail/display.png" alt="" />
              </router-link>

              <router-link
                v-if="
                  project &&
                  type.is_editable &&
                  permissions &&
                  permissions.can_create_feature_type &&
                  isOnline
                "
                :to="{
                  name: 'editer-type-signalement',
                  params: { slug_type_signal: type.slug },
                }"
                class="ft-icon-btn"
                data-tooltip="Éditer le type de signalement"
                data-position="top center"
                data-variation="mini"
                :data-test="`edit-feature-type-for-${type.title}`"
              >
                <img src="@/assets/img/project-detail/settings.png" alt="" />
              </router-link>

              <a
                v-if="isProjectAdmin && isOnline"
                class="ft-icon-btn"
                data-tooltip="Supprimer le type de signalement"
                data-position="top center"
                data-variation="mini"
                :data-test="`delete-feature-type-for-${type.title}`"
                @click="toggleDeleteFeatureType(type)"
              >
                <img src="@/assets/img/project-card/delete.png" alt="" />
              </a>
            </template>
          </div>
        </div>
      </div>
      <div v-if="feature_types.length === 0">
        <em> Le projet ne contient pas encore de type de signalements. </em>
      </div>
    </div>

    <div class="feature-types-panel__help">
      <button
        type="button"
        class="ui small button circular compact icon"
        data-tooltip="Consulter la documentation"
        data-position="bottom right"
        data-variation="mini"
        data-test="read-doc"
        @click="goToDocumentation"
      >
        <i class="question icon" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

import FeatureTypeLink from "@/components/FeatureType/FeatureTypeLink";

export default {
  name: "ProjectFeatureTypes",

  components: {
    FeatureTypeLink,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  data() {
    return {
      slug: this.$route.params.slug,
      fetchCallCounter: 0,
      hadPending: false,
    };
  },

  computed: {
    ...mapState(["configuration", "isOnline", "user_permissions"]),
    ...mapState("feature-type", ["feature_types", "importFeatureTypeData"]),
    ...mapGetters(["permissions"]),

    isProjectAdmin() {
      return (
        this.user_permissions &&
        this.user_permissions[this.slug] &&
        this.user_permissions[this.slug].is_project_administrator
      );
    },
  },

  mounted() {
    this.fetchImports();
  },

  methods: {
    ...mapActions("feature-type", ["GET_IMPORTS"]),

    fetchImports() {
      this.fetchCallCounter += 1; // register each time function is programmed to be called in order to avoid redundant calls
      this.GET_IMPORTS({
        project_slug: this.$route.params.slug,
      }).then((response) => {
        if (
          response.data &&
          response.data.some((el) => el.status === "pending")
        ) {
          this.hadPending = true; // store pending import to know if project need to be updated, after mounted
          // if there is still some pending imports re-fetch imports by calling this function again
          setTimeout(() => {
            if (this.fetchCallCounter <= 1) {
              // if the function wasn't called more than once in the reload interval, then call it again
              this.fetchImports();
            }
            this.fetchCallCounter -= 1; // decrease function counter
          }, this.$store.state.configuration.VUE_APP_RELOAD_INTERVAL);
        } else if (this.hadPending) {
          // if no more pending import, get last features
          this.$emit("update");
        }
      });
    },

    isImporting(type) {
      if (this.importFeatureTypeData) {
        const singleImportData = this.importFeatureTypeData.find(
          (el) => el.feature_type_title === type.slug,
        );
        return singleImportData && singleImportData.status === "pending";
      }
      return false;
    },

    goToDocumentation() {
      window.open(this.configuration.VUE_APP_URL_DOCUMENTATION);
    },

    toggleDeleteFeatureType(featureType) {
      this.$emit("delete", featureType);
    },
  },
};
</script>

<style>
/* // ! missing style in semantic.min.css */
.ui.right.floated.button {
  float: right;
}
</style>

<style lang="less" scoped>
.feature-type-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .feature-type-title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.5em;
  }
  & > .middle.aligned.content {
    display: flex;
  }
}

.feature-types-panel__help {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.feature-types-panel__head {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: -1rem -1rem 0.85rem;
  padding: 0.7rem 1rem;
  border-radius: 6px 6px 0 0;
  background: #313c4e;
  color: #fff;
  overflow: visible;

  h3 {
    margin: 0;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.feature-types-panel__add {
  position: relative;
  z-index: 6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 4px;
  color: #fff;
  font-size: 1.35rem;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;

  &:before,
  &:after {
    z-index: 20;
  }

  img {
    width: 14px;
    height: 14px;
    object-fit: contain;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
}

.ft-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }

  &:hover {
    background: #eef0f6;
  }
}

.import-message {
  color: var(--primary-highlight-color, #008c86);
  white-space: nowrap;
  padding: 0.25em;
  display: flex;
  align-items: center;
  & > i {
    height: auto;
  }
}
</style>
